import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Package, Wallet, Star, Store, Edit2 } from "lucide-react";

const STATUS_LABELS = { pending: "Pending", accepted: "Accepted", picked_up: "Picked Up", washing: "Washing", ready: "Ready", delivered: "Delivered", cancelled: "Cancelled" };
const STATUS_COLORS = { pending: "bg-yellow-100 text-yellow-800", accepted: "bg-blue-100 text-blue-800", picked_up: "bg-indigo-100 text-indigo-800", washing: "bg-purple-100 text-purple-800", ready: "bg-emerald-100 text-emerald-800", delivered: "bg-green-100 text-green-800", cancelled: "bg-red-100 text-red-800" };

const NEXT_STATUS = {
  pending: "accepted",
  accepted: "picked_up",
  picked_up: "washing",
  washing: "ready",
  ready: "delivered",
};

export default function PartnerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [orders, setOrders] = useState([]);
  const [showShopForm, setShowShopForm] = useState(false);
  const [shopForm, setShopForm] = useState({
    shop_name: "", description: "", address: "", locality: "", phone: "",
    services: ["Wash & Fold", "Dry Clean", "Ironing"], price_per_kg: 80,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "partner") {
      navigate("/login");
      return;
    }
    refresh();
  }, [user, loading, navigate]);

  const refresh = () => {
    api.get("/partners/me/shop").then((r) => {
      if (r.data) {
        setShop(r.data);
        setShopForm({
          shop_name: r.data.shop_name, description: r.data.description, address: r.data.address,
          locality: r.data.locality, phone: r.data.phone, services: r.data.services, price_per_kg: r.data.price_per_kg,
        });
      } else {
        setShowShopForm(true);
      }
    });
    api.get("/orders").then((r) => setOrders(r.data));
  };

  const saveShop = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/partners/shop", shopForm);
      setShowShopForm(false);
      refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const advance = async (orderId, status) => {
    await api.put(`/orders/${orderId}/status`, { status });
    refresh();
  };

  if (loading || !user || user.role !== "partner") return null;

  const stats = {
    active: orders.filter((o) => !["delivered", "cancelled"].includes(o.status)).length,
    delivered: orders.filter((o) => o.status === "delivered").length,
    earnings: orders.filter((o) => o.status === "delivered").reduce((s, o) => s + o.total_amount * 0.8, 0),
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2 text-[#C2410C]">Partner Dashboard</div>
            <h1 className="font-display font-black text-4xl tracking-tight">{shop ? shop.shop_name : `Welcome, ${user.name}`}</h1>
            {shop && <p className="text-muted-foreground mt-2">{shop.locality}, {shop.city}</p>}
          </div>
          {shop && (
            <button onClick={() => setShowShopForm(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-border rounded-lg font-semibold text-sm hover:bg-secondary" data-testid="edit-shop-button">
              <Edit2 className="w-4 h-4" /> Edit Shop
            </button>
          )}
        </div>

        {shop && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatCard icon={Package} label="Active Orders" value={stats.active} color="#1E3A8A" />
            <StatCard icon={Star} label="Delivered" value={stats.delivered} color="#C2410C" />
            <StatCard icon={Wallet} label="Earnings (Net)" value={`₹${stats.earnings.toFixed(0)}`} color="#059669" subtext="After 20% platform fee" />
          </div>
        )}

        {showShopForm && (
          <div className="bg-white border border-border rounded-2xl p-6 mb-8" data-testid="shop-form">
            <h2 className="font-display font-bold text-2xl mb-4">{shop ? "Edit your shop" : "Set up your shop"}</h2>
            {error && <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}
            <form onSubmit={saveShop} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Shop name" value={shopForm.shop_name} onChange={(v) => setShopForm({ ...shopForm, shop_name: v })} required testId="shop-name-input" />
              <Input label="Phone" value={shopForm.phone} onChange={(v) => setShopForm({ ...shopForm, phone: v })} testId="shop-phone-input" />
              <Input label="Locality" value={shopForm.locality} onChange={(v) => setShopForm({ ...shopForm, locality: v })} testId="shop-locality-input" />
              <Input label="Price per kg (₹)" type="number" value={shopForm.price_per_kg} onChange={(v) => setShopForm({ ...shopForm, price_per_kg: parseFloat(v) || 0 })} testId="shop-price-input" />
              <div className="md:col-span-2">
                <label className="text-sm font-semibold mb-1.5 block">Address</label>
                <input value={shopForm.address} onChange={(e) => setShopForm({ ...shopForm, address: e.target.value })} required className="w-full px-4 py-2.5 rounded-lg border border-border" data-testid="shop-address-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold mb-1.5 block">Description</label>
                <textarea value={shopForm.description} onChange={(e) => setShopForm({ ...shopForm, description: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-lg border border-border" data-testid="shop-description-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold mb-1.5 block">Services (comma-separated)</label>
                <input
                  value={shopForm.services.join(", ")}
                  onChange={(e) => setShopForm({ ...shopForm, services: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  className="w-full px-4 py-2.5 rounded-lg border border-border"
                  data-testid="shop-services-input"
                />
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" disabled={submitting} className="px-6 py-3 rounded-lg bg-[#1E3A8A] text-white font-bold tactile-btn disabled:opacity-60" data-testid="save-shop-button">
                  {submitting ? "Saving..." : "Save Shop"}
                </button>
                {shop && (
                  <button type="button" onClick={() => setShowShopForm(false)} className="px-6 py-3 rounded-lg bg-secondary font-bold">Cancel</button>
                )}
              </div>
            </form>
          </div>
        )}

        {shop && (
          <>
            <h2 className="font-display font-bold text-2xl mb-4">Orders</h2>
            <div className="space-y-3" data-testid="partner-orders-list">
              {orders.length === 0 && (
                <div className="text-center py-16 bg-white rounded-2xl border border-border">
                  <Store className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <div className="font-semibold">No orders yet</div>
                  <div className="text-sm text-muted-foreground">When customers book, orders will appear here.</div>
                </div>
              )}
              {orders.map((o) => (
                <div key={o.id} className="bg-white rounded-xl border border-border p-5" data-testid={`partner-order-${o.id}`}>
                  <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <div className="font-bold text-lg">{o.customer_name}</div>
                      <div className="text-xs text-muted-foreground">Order #{o.id.slice(0, 8)} • {new Date(o.created_at).toLocaleString()}</div>
                      {o.customer_phone && <div className="text-xs text-muted-foreground">📞 {o.customer_phone}</div>}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span>
                  </div>
                  <div className="text-sm mb-2"><strong>Pickup:</strong> {o.pickup_address}</div>
                  <div className="text-sm text-muted-foreground mb-3">Scheduled: {new Date(o.pickup_date).toLocaleString()}</div>
                  <div className="text-sm border-t border-border pt-3 mb-3">
                    {o.items.map((it, i) => (
                      <div key={i} className="flex justify-between"><span>{it.service} × {it.quantity}</span><span>₹{(it.quantity * it.unit_price).toFixed(2)}</span></div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-3 flex-wrap gap-2">
                    <div className="font-bold">Total: ₹{o.total_amount.toFixed(2)} <span className="text-xs text-muted-foreground font-normal">(You earn ₹{(o.total_amount * 0.8).toFixed(2)})</span></div>
                    {NEXT_STATUS[o.status] && (
                      <button
                        onClick={() => advance(o.id, NEXT_STATUS[o.status])}
                        className="px-4 py-2 rounded-lg bg-[#C2410C] text-white font-semibold text-sm tactile-btn-accent"
                        data-testid={`advance-order-${o.id}`}
                      >
                        Mark as {STATUS_LABELS[NEXT_STATUS[o.status]]} →
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, subtext }) {
  return (
    <div className="bg-white rounded-xl border border-border p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="font-display font-black text-3xl" style={{ color }}>{value}</div>
      {subtext && <div className="text-xs text-muted-foreground mt-1">{subtext}</div>}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-sm font-semibold mb-1.5 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full px-4 py-2.5 rounded-lg border border-border" data-testid={testId} />
    </div>
  );
}
